import React from 'react';
import { format } from 'date-fns';
import api from '../services/api.ts';
import { GitHubLogo } from './icons/GitHubLogo.js';
import { SlackLogo } from './icons/SlackLogo.js';
import { NotionLogo } from './icons/NotionLogo.js';
import { DiscordLogo } from './icons/DiscordLogo.js';
import { Calendar, FileText, GitBranch, Activity } from 'react-feather';

interface Activity {
  type: string;
  timestamp: string;
  details: {
    title: string;
    id: string;
    url?: string;
    content?: string;
    state?: string;
    number?: number;
    repository?: string;
    text?: string;
    channel?: {
      name: string;
      id: string;
    };
    user?: {
      name: string;
      avatar?: string;
    };
    reactions?: Array<{
      name: string;
      count: number;
      users: string[];
    }>;
    space?: {
      name: string;
    };
    version?: number;
    labels?: string[];
    last_updated_by?: {
      name: string;
    };
    last_edited_by?: {
      name: string;
    };
    author?: {
      username: string;
      discriminator: string;
    };
    guild?: {
      name: string;
    };
    [key: string]: any;
  };
}

interface GroupedActivities {
  [key: string]: {
    name: string;
    icon: React.ReactNode;
    activities: Activity[];
  }
}

export default function ActivityFeed() {
  const [activities, setActivities] = React.useState<Activity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const getServiceIcon = (service: string) => {
    switch (service.toLowerCase()) {
      case 'github':
        return <GitHubLogo className="w-5 h-5" />;
      case 'slack':
        return <SlackLogo className="w-5 h-5" />;
      case 'discord':
        return <DiscordLogo className="w-5 h-5 text-[#5865F2]" />;
      case 'jira':
        return <Calendar className="w-5 h-5 text-blue-600" />;
      case 'confluence':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'linear':
        return <GitBranch className="w-5 h-5 text-indigo-600" />;
      case 'notion':
        return <NotionLogo className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5 text-gray-600" />;
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      
      const responses = await Promise.allSettled([
        api.get('/github/activity'),
        api.get('/slack/activity'),
        api.get('/discord/activity'),
        api.get('/jira/activity'),
        api.get('/confluence/activity'),
        api.get('/linear/activity'),
        api.get('/notion/activity')
      ]);
      
      // Debug log raw Slack response
      const slackResponse = responses[1];
      if (slackResponse.status === 'fulfilled') {
        console.log('Raw Slack response:', slackResponse.value.data);
        // Add this debug log
        slackResponse.value.data?.forEach((item: any) => {
          console.log('Slack item type:', item.type);
          console.log('Slack item details:', item.details);
        });
      }

      // Process successful responses
      const allActivities = responses
        .filter((response): response is PromiseFulfilledResult<any> => 
          response.status === 'fulfilled' && 
          response.value?.data !== undefined
        )
        .flatMap(response => {
          const data = response.value.data;
          if (!Array.isArray(data)) {
            console.log('Non-array data:', data);
            return [];
          }
          
          return data.filter((activity): activity is Activity => {
            // More lenient validation for Slack messages
            if (activity?.type?.startsWith('slack_')) {
              console.log('Processing Slack activity:', activity);
              const isValid = activity && 
                typeof activity === 'object' &&
                'timestamp' in activity &&
                'details' in activity &&
                typeof activity.details === 'object' &&
                // Allow either title or text for Slack messages
                (activity.details.title || activity.details.text);
              
              if (!isValid) {
                console.log('Invalid Slack activity:', activity);
              }
              return isValid;
            }

            // Regular validation for other types
            const isValid = activity && 
              typeof activity === 'object' &&
              'type' in activity &&
              'timestamp' in activity &&
              'details' in activity &&
              typeof activity.details === 'object' &&
              'title' in activity.details;
            
            if (!isValid) {
              console.log('Invalid activity:', activity);
            }
            return isValid;
          });
        });

      console.log('All processed activities:', allActivities);
      const slackActivities = allActivities.filter(a => a.type?.startsWith('slack_'));
      console.log('Final Slack activities:', slackActivities);

      // Sort by timestamp
      allActivities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setActivities(allActivities);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  // Group activities by service
  const groupedActivities = React.useMemo(() => {
    if (!activities?.length) return {};

    return activities.reduce((acc: GroupedActivities, activity) => {
      if (!activity?.type) return acc;

      const [service] = activity.type.split('_');
      const group = service.charAt(0).toUpperCase() + service.slice(1);
      
      if (!acc[group]) {
        acc[group] = {
          name: group,
          icon: getServiceIcon(group),
          activities: []
        };
      }

      acc[group].activities.push(activity);
      return acc;
    }, {});
  }, [activities]);

  const renderActivity = (activity: Activity) => {
    if (!activity?.type || !activity?.details) {
      return null;
    }

    // For Slack, we need either title or text
    if (activity.type.startsWith('slack_') && !activity.details.text && !activity.details.title) {
      return null;
    }
    
    // For other types, we need title
    if (!activity.type.startsWith('slack_') && !activity.details.title) {
      return null;
    }

    const commonClasses = "flex items-start space-x-3 p-4 hover:bg-gray-700/50 transition-colors min-h-[5rem]";
    const commonIconClasses = "w-5 h-5 flex-shrink-0 mt-0.5";
    const commonTitleClasses = "text-sm font-medium text-gray-200 truncate group-hover:text-blue-400";
    const commonTimestampClasses = "text-xs text-gray-400 whitespace-nowrap";
    const commonContentClasses = "mt-1 text-sm text-gray-400 line-clamp-2";
    const commonMetaClasses = "mt-1 text-xs text-gray-500";
    const commonBadgeClasses = "mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";

    const getStatusBadgeClasses = (color?: string) => 
      `${commonBadgeClasses} ${color ? `bg-${color}-900/50 text-${color}-200` : 'bg-blue-900/50 text-blue-200'}`;

    switch (activity.type) {
      case 'github_issue':
      case 'github_pr':
        return (
          <div className={`${commonClasses} group cursor-pointer`}
               onClick={() => activity.details.url && window.open(activity.details.url, '_blank')}>
            <GitHubLogo className={commonIconClasses} />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {activity.details.state && (
                  <span className={getStatusBadgeClasses(
                    activity.details.state === 'open' ? 'green' : 'red'
                  )}>
                    {activity.details.state}
                  </span>
                )}
              </div>
              {activity.details.repository && (
                <p className={commonMetaClasses}>in {activity.details.repository}</p>
              )}
            </div>
          </div>
        );
      case 'slack_message':
      case 'slack_channel':
        return (
          <div className={commonClasses}>
            <SlackLogo className={commonIconClasses} />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>
                  {activity.details.user?.name || 'Unknown User'}
                </p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              <p className={commonContentClasses}>
                {activity.details.text || activity.details.title || 'No message content'}
              </p>
              <div className="flex items-center gap-2">
                {activity.details.channel && (
                  <span className={commonMetaClasses}>
                    #{activity.details.channel.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      case 'discord_message':
        return (
          <div className={commonClasses}>
            <DiscordLogo className={`${commonIconClasses} text-[#5865F2]`} />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>
                  {activity.details.author?.username || 'Unknown User'}
                  {activity.details.author?.discriminator && 
                    `#${activity.details.author.discriminator}`}
                </p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              <p className={commonContentClasses}>
                {activity.details.content}
              </p>
              <div className="flex items-center gap-2">
                {activity.details.guild && (
                  <span className={commonMetaClasses}>
                    {activity.details.guild.name}
                  </span>
                )}
                {activity.details.channel && (
                  <span className={commonMetaClasses}>
                    #{activity.details.channel.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      case 'jira_issue':
        return (
          <div className={commonClasses}>
            <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              {activity.details.status && (
                <span className={getStatusBadgeClasses(
                  activity.details.status.name === 'Open' ? 'green' : 'red'
                )}>
                  {activity.details.status.name}
                </span>
              )}
            </div>
          </div>
        );
      case 'confluence_page':
        return (
          <div className={commonClasses}>
            <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              {activity.details.content && (
                <p className={commonContentClasses}>{activity.details.content}</p>
              )}
              {activity.details.space && (
                <p className={commonMetaClasses}>in {activity.details.space.name}</p>
              )}
            </div>
          </div>
        );
      case 'linear_issue':
        return (
          <div className={`${commonClasses} cursor-pointer`}
               onClick={() => activity.details.url && window.open(activity.details.url, '_blank')}>
            <GitBranch className="w-5 h-5 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {activity.details.status && (
                  <span className={getStatusBadgeClasses(
                    activity.details.status.name === 'Open' ? 'green' : 'red'
                  )}>
                    {activity.details.status.name}
                  </span>
                )}
                {activity.details.priority && (
                  <span className={commonMetaClasses}>P{activity.details.priority}</span>
                )}
              </div>
              {activity.details.team && (
                <p className={commonMetaClasses}>{activity.details.team.name} / {activity.details.identifier}</p>
              )}
            </div>
          </div>
        );
      case 'notion_page':
        return (
          <div 
            className={`${commonClasses} cursor-pointer`}
            onClick={() => activity.details.url && window.open(activity.details.url, '_blank')}
          >
            <NotionLogo className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
              {activity.details.content && (
                <p className={commonContentClasses}>{activity.details.content}</p>
              )}
              {activity.details.last_edited_by && (
                <p className={commonMetaClasses}>edited by {activity.details.last_edited_by.name}</p>
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className={commonClasses}>
            <Activity className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between">
                <p className={commonTitleClasses}>{activity.details.title}</p>
                <span className={commonTimestampClasses}>
                  {format(new Date(activity.timestamp), 'MMM d, yyyy')}
                </span>
              </div>
            </div>
          </div>
        );
    }
  };

  React.useEffect(() => {
    fetchActivities();
  }, []);

  if (loading) return <div className="text-gray-400">Loading activities...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  // Split activities into two columns
  const services = Object.entries(groupedActivities);
  const midpoint = Math.ceil(services.length / 2);
  const leftColumn = services.slice(0, midpoint);
  const rightColumn = services.slice(midpoint);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-100">Recent Activity</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {leftColumn.map(([service, { name, icon, activities }]) => (
            <div key={service} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-700 border-b border-gray-600">
                <div className="flex items-center gap-2">
                  {icon}
                  <h3 className="font-medium text-gray-100">{name}</h3>
                  <span className="text-sm text-gray-400">({activities.length})</span>
                </div>
              </div>
              
              <div className="divide-y divide-gray-700">
                {activities.map((activity, index) => (
                  <div key={`${activity.type}-${activity.details.id}-${index}`}>
                    {renderActivity(activity)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {rightColumn.map(([service, { name, icon, activities }]) => (
            <div key={service} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-700 border-b border-gray-600">
                <div className="flex items-center gap-2">
                  {icon}
                  <h3 className="font-medium text-gray-100">{name}</h3>
                  <span className="text-sm text-gray-400">({activities.length})</span>
                </div>
              </div>
              
              <div className="divide-y divide-gray-700">
                {activities.map((activity, index) => (
                  <div key={`${activity.type}-${activity.details.id}-${index}`}>
                    {renderActivity(activity)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {services.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          No recent activity to display
        </div>
      )}
    </div>
  );
} 